import React, { useEffect, useState } from "react";
import { FileArrowUp, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout, { StatusPill } from "@/components/MerchantLayout";
import { mApi } from "@/lib/api";

const DOC_TYPES = [
  { v: "incorporation", l: "Certificate of Incorporation" },
  { v: "director_id", l: "Director ID / Passport" },
  { v: "proof_of_address", l: "Proof of Address" },
  { v: "bank_statement", l: "Bank Statement" },
  { v: "other", l: "Other" },
];

export default function MerchantKyb() {
  const [items, setItems] = useState([]);
  const [docType, setDocType] = useState("incorporation");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [merchantStatus, setMerchantStatus] = useState(null);

  const load = async () => {
    const [k, me] = await Promise.all([mApi.get("/merchant/kyb"), mApi.get("/merchant/me")]);
    setItems(k.data.items);
    setMerchantStatus(me.data.merchant);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      await mApi.post("/merchant/kyb/upload", {
        doc_type: docType,
        file_name: file.name,
        content_base64: b64,
      });
      toast.success(`${file.name} uploaded`);
      setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="m-kyb-page">
        <div className="mb-8">
          <h1 className="font-display text-3xl tracking-tight font-medium">KYB Documents</h1>
          <p className="mt-1 text-sm text-zinc-500">Submit verification documents to activate live card issuance.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <StatusCard
            label="Pending"
            count={items.filter((i) => i.status === "submitted").length}
            icon={Clock} color="amber"
          />
          <StatusCard
            label="Approved"
            count={items.filter((i) => i.status === "approved").length}
            icon={CheckCircle} color="emerald"
          />
          <StatusCard
            label="Rejected"
            count={items.filter((i) => i.status === "rejected").length}
            icon={XCircle} color="red"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-sm p-5">
            <div className="font-display text-lg tracking-tight font-medium mb-4">Upload document</div>
            <form onSubmit={upload} className="space-y-3" data-testid="kyb-upload-form">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Document type</label>
                <select
                  data-testid="kyb-doc-type"
                  value={docType} onChange={(e) => setDocType(e.target.value)}
                  className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                >
                  {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">File (max 10MB)</label>
                <input
                  type="file" data-testid="kyb-file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-zinc-200 file:bg-white file:text-sm file:text-zinc-900 hover:file:bg-zinc-50"
                />
              </div>
              <button
                disabled={!file || busy}
                data-testid="kyb-upload-btn"
                className="w-full h-9 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
              >
                <FileArrowUp size={14} /> {busy ? "Uploading…" : "Upload"}
              </button>
            </form>

            {merchantStatus?.kyb_status === "rejected" && merchantStatus?.kyb_rejection_reason && (
              <div className="mt-5 p-3 rounded-sm bg-red-50 border border-red-200 text-xs text-red-800">
                <div className="font-semibold mb-1">Last review: rejected</div>
                {merchantStatus.kyb_rejection_reason}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                  <th className="px-5 py-3">File</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Size</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-zinc-50">
                    <td className="px-5 py-3 font-medium">{d.file_name}</td>
                    <td className="px-5 py-3 text-xs">{d.doc_type}</td>
                    <td className="px-5 py-3 text-xs font-mono">{Math.round(d.size_bytes / 1024)} KB</td>
                    <td className="px-5 py-3"><StatusPill value={d.status} /></td>
                    <td className="px-5 py-3 text-xs text-zinc-500">{(d.uploaded_at || "").slice(0, 19).replace("T", " ")}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-xs text-zinc-500">No documents uploaded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}

function StatusCard({ label, count, icon: Icon, color }) {
  const cls = { amber: "text-amber-600 bg-amber-50", emerald: "text-emerald-600 bg-emerald-50", red: "text-red-600 bg-red-50" }[color];
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</div>
          <div className="font-display text-3xl tracking-tight font-medium mt-2 tabular-nums">{count}</div>
        </div>
        <div className={`h-8 w-8 rounded-sm flex items-center justify-center ${cls}`}><Icon size={16} /></div>
      </div>
    </div>
  );
}
